import type { ReactNode } from 'react';
import type { ProjectPageLoader } from '@modules/project/type';

export const ACHIPRO_PHOTOGRAPHER_SPLIT = '[__ACHIPRO_PHOTOGRAPHER_SPLIT__]';

// Helper function to singularize a string (basic implementation for common cases)
const singularizeString = (text: string): string => {
    if (text.endsWith('ies')) {
        return text.slice(0, -3) + 'y';
    }
    if (text.endsWith('es')) {
        return text.slice(0, -2);
    }
    if (text.endsWith('s')) {
        return text.slice(0, -1);
    }
    return text;
};

interface DetailsItems {
    itemName: string;
    itemNameDesktop?: string;
    value: string;
    link?: string;
    isExternal?: boolean;
}

interface Photographer {
    ID?: number;
    Title?: string | null;
    Link?: string | null;
    Website?: string | null;
}

export type DetailsItemsData = Partial<
    Pick<
        ProjectPageLoader['project'],
        | 'Title'
        | 'Professional'
        | 'Categories'
        | 'Location'
        | 'YearOfCompletion'
        | 'ProjectPriceRange'
        | 'BuildingStyle'
        | 'Client'
        | 'LibraryDownloads'
        | 'Awards'
    > & { Photographers?: Photographer[] | null }
>;

const truncateText = (text: string) => {
    if (!text || text.length <= 35) {
        return text ?? '';
    }
    let wordLength = 0;
    const textArray = text.split(' ')?.map((word, index) => {
        wordLength += word.length + 1;
        return {
            word,
            length: word.length,
            wordLength,
            index,
        };
    });

    const sortedTextArray = [...textArray].sort(
        (a, b) => b.wordLength - a.wordLength
    );
    const index =
        sortedTextArray.find((item) => item.wordLength <= 35)?.index || 0;

    return (
        textArray
            .slice(0, index + 1)
            .map((item) => item.word)
            .join(' ') + '...'
    );
};

export const getDetailsItems = (
    data: DetailsItemsData,
    {
        includeAddress = false,
        includeDownloads = true,
        includeNewFields = true,
    }: {
        includeAddress?: boolean;
        includeDownloads?: boolean;
        includeNewFields?: boolean;
    }
): DetailsItems[] => {
    const detailsItems = [];

    if (data.Title && includeNewFields) {
        detailsItems.push({
            itemName: 'Title',
            value: data.Title,
        });
    }

    if (data.Professional && includeNewFields) {
        const professionalCategory = data.Professional.Categories?.[0];
        const singularCategoryTitle = professionalCategory?.PrettyTitle
            ? singularizeString(professionalCategory.PrettyTitle)
            : 'Professional';

        detailsItems.push({
            itemName: singularCategoryTitle,
            value: data.Professional.Title,
            link: data.Professional.Link,
        });
    }

    if (data.Categories && data.Categories.length !== 0 && includeNewFields) {
        const category = data.Categories[0];

        if (category) {
            const parents =
                category.Parents?.filter((parent) => !!parent).map(
                    (parent) => parent?.PrettyTitle || ''
                ) || [];
            const self = category.PrettyTitle;
            parents.push(self);

            const links = category.Parents?.map((parent) => parent?.Link) || [];
            links.push(category.Link);

            detailsItems.push({
                itemName: 'Category',
                value: parents.join(),
                link: links.join(),
            });
        }
    }

    if (includeAddress && data.Location) {
        detailsItems.push({
            itemName: 'Region',
            value: [
                data.Location.Suburb,
                data.Location.Region,
                data.Location.Country,
            ]
                .filter((t) => (t ?? '').trim().length > 0)
                .join(', '),
        });
    }

    if (data.YearOfCompletion && data.YearOfCompletion !== 0) {
        detailsItems.push({
            itemName: 'Year of completion',
            itemNameDesktop: 'Completed',
            value: String(data.YearOfCompletion),
        });
    }

    if (data.ProjectPriceRange) {
        detailsItems.push({
            itemName: 'Price range to build',
            itemNameDesktop: 'Price range',
            value: data.ProjectPriceRange,
        });
    }

    if (data.BuildingStyle) {
        detailsItems.push({
            itemName: 'Building style',
            value: data.BuildingStyle.replaceAll('_', ' '),
        });
    }

    if (data.Client) {
        detailsItems.push({
            itemName: 'Client',
            value: data.Client,
        });
    }

    if (data.Awards && data.Awards.length !== 0) {
        data.Awards.forEach((award, index) => {
            detailsItems.push({
                itemName: index === 0 ? 'Awards' : '',
                value: `${award.title} ${award.year ? `- ${award.year}` : ''}`,
            });
        });
    }

    if (
        data.Photographers &&
        data.Photographers.length !== 0 &&
        includeNewFields
    ) {
        const photographers = data.Photographers.map(
            (photographer: Photographer) => {
                const title = photographer.Title || '';
                title.replaceAll(ACHIPRO_PHOTOGRAPHER_SPLIT, '');
                return truncateText(title);
            }
        );
        const links = data.Photographers.map((photographer: Photographer) => {
            const link = photographer.Link || photographer.Website || '';
            link.replaceAll(ACHIPRO_PHOTOGRAPHER_SPLIT, '');
            return link;
        });

        detailsItems.push({
            itemName: 'Photographers',
            value: photographers.join(ACHIPRO_PHOTOGRAPHER_SPLIT),
            link: links.join(ACHIPRO_PHOTOGRAPHER_SPLIT),
        });
    }

    if (
        includeDownloads &&
        data.LibraryDownloads &&
        data.LibraryDownloads.length !== 0
    ) {
        const fileCategories = new Map<string, boolean>();
        data.LibraryDownloads.forEach((item) => {
            const categoryName = item.FileCategory?.Title ?? 'Plans';
            const label = fileCategories.has(categoryName) ? '' : categoryName;
            fileCategories.set(categoryName, true);

            detailsItems.push({
                itemName: label,
                value: item.Title,
                link: item.InternalLink || item.ExternalLink,
                isExternal: !!item.ExternalLink,
            });
        });
    }

    return detailsItems;
};

export const displayItems = (
    items: DetailsItems[],
    showItem: (
        label: string,
        value: string,
        link?: string,
        isExternal?: boolean
    ) => ReactNode
) => {
    return items.map((item) => {
        return showItem(item.itemName, item.value, item.link, item.isExternal);
    });
};
